// NOTE: Must be the first import in src/index.jsx. Sentry.init has to run before any module calls
// Sentry.withSentryReactRouterV7Routing, which otherwise silently returns the uninstrumented Routes.
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { truncateLargeFields } from "./VoiceChat/utils/helpers";

// To test Sentry on local dev server, manually run e.g. `VITE_SENTRY_DEV=true npm run start:dev`
if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DEV === "true") {
  const sampleRate = parseFloat(import.meta.env.VITE_SENTRY_TRACING_SAMPLE_RATE || "1.0");
  const tracePropagationTargets = ["localhost", /^https:\/\/([a-z0-9-]+\.)+fictivereality\.com/i];
  const integrations = [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // this may double report errors as React Error Boundary already captures errors
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
    Sentry.feedbackIntegration({
      // Disable the injection of the default widget
      autoInject: false,
    }),
  ];
  // Enable Replay whenever Sentry is initialized (not just PROD) so VITE_SENTRY_DEV
  // testing also attaches replays to feedback submissions.
  if (import.meta.env.PROD || import.meta.env.VITE_SENTRY_DEV === "true") {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: false, // Runs on our domain, we assume no personal data
        blockAllMedia: true, // saves space?
      })
    );
  }
  // Set if running from output of npm run build
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: integrations,
    release: "WebApp@" + import.meta.env.VITE_VERSION,
    environment: import.meta.env.VITE_SENTRY_DEV ? "local-dev" : import.meta.env.VITE_PROJECT_ID,
    beforeSend: event => {
      // The feedback widget doesn't auto-link to lastEventId, so feedback shows no
      // breadcrumbs. Link it to the most recent event (a real error or the
      // synthetic snapshot from toggleErrorReporting) via associated_event_id.
      if (event.type === "feedback") {
        if (event.contexts?.feedback && !event.contexts.feedback.associated_event_id) {
          const lastId = Sentry.lastEventId();
          if (lastId) {
            event.contexts.feedback.associated_event_id = lastId;
          }
        }
        return event;
      }
      console.log("Reporting error to sentry", event);
      return event; // Return event so it will be sent
    },
    beforeBreadcrumb: breadcrumb => {
      // Need this because sometimes we log large objects which will be declined by Sentry
      if (!breadcrumb) return null;

      return truncateLargeFields(breadcrumb) ?? null;
    },
    tracesSampleRate: sampleRate,
    sendDefaultPii: false,
    tracePropagationTargets,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
  });
  Sentry.setTag("deploymentEnvironment", import.meta.env.VITE_DEPLOYMENT_ENV);
  Sentry.setTag("frontendTarget", import.meta.env.VITE_FRONTEND_TARGET);
  console.log(`Initiated Sentry error tracing with sample rate ${sampleRate}`, tracePropagationTargets);
}
